import type { AgentConfiguratorState, AgentWorker } from '../types/agentConfigurator';
import { normalizeOptimizationApproach } from '../types/optimizationApproach';

export type FieldDecision = 'accepted' | 'rejected' | 'pending';

export interface ConfigFieldDiff {
  path: string;
  label: string;
  oldValue: string;
  newValue: string;
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  return String(value);
}

function pushIfChanged(
  out: ConfigFieldDiff[],
  path: string,
  label: string,
  oldVal: unknown,
  newVal: unknown
) {
  const oldValue = formatValue(oldVal);
  const newValue = formatValue(newVal);
  if (oldValue !== newValue) {
    out.push({ path, label, oldValue, newValue });
  }
}

export function workerPathKey(w: AgentWorker): string {
  return (w.id && String(w.id).trim()) || w.name || 'unknown';
}

function workerKey(w: AgentWorker): string {
  return workerPathKey(w);
}

function findWorker(workers: AgentWorker[], key: string): AgentWorker | undefined {
  return workers.find((w) => workerKey(w) === key);
}

const TOOL_FIELDS = [
  'use_rag',
  'use_ltm',
  'use_diary_read',
  'use_diary_write',
  'use_portrait_read',
  'use_portrait_write',
  'use_config_patch_tool',
  'use_workflow_patch_tool',
] as const;

type ToolField = typeof TOOL_FIELDS[number];

function toolFieldLabel(field: ToolField): string {
  switch (field) {
    case 'use_rag':
      return 'RAG';
    case 'use_ltm':
      return 'LTM';
    case 'use_diary_read':
      return 'Diary read';
    case 'use_diary_write':
      return 'Diary write';
    case 'use_portrait_read':
      return 'Portrait read';
    case 'use_portrait_write':
      return 'Portrait write';
    case 'use_config_patch_tool':
      return 'Config patch tool';
    case 'use_workflow_patch_tool':
      return 'Workflow patch tool';
  }
}

function boolValue(value: unknown): boolean {
  return value === true;
}

function pushToolDiffs(
  out: ConfigFieldDiff[],
  prefixPath: string,
  labelPrefix: string,
  oldAgent: Partial<Record<ToolField, unknown>>,
  newAgent: Partial<Record<ToolField, unknown>>
) {
  TOOL_FIELDS.forEach((field) => {
    pushIfChanged(
      out,
      `${prefixPath}.${field}`,
      `${labelPrefix} — ${toolFieldLabel(field)}`,
      boolValue(oldAgent[field]),
      boolValue(newAgent[field])
    );
  });
}

/** Semantic diff between two agent configs for configurator field highlights. */
export function buildConfigFieldDiffs(
  base: AgentConfiguratorState,
  proposed: AgentConfiguratorState
): ConfigFieldDiff[] {
  const out: ConfigFieldDiff[] = [];

  const c0 = base.coordinator;
  const c1 = proposed.coordinator;
  pushIfChanged(out, 'coordinator.name', 'Coordinator — name', c0.name, c1.name);
  pushIfChanged(out, 'coordinator.description', 'Coordinator — description', c0.description, c1.description);
  pushIfChanged(out, 'coordinator.prompt', 'Coordinator — prompt', c0.prompt, c1.prompt);
  pushIfChanged(out, 'coordinator.model', 'Coordinator — model', c0.model, c1.model);
  pushIfChanged(out, 'coordinator.temperature', 'Coordinator — temperature', c0.temperature, c1.temperature);
  pushIfChanged(
    out,
    'coordinator.models_reasoning',
    'Coordinator — reasoning',
    c0.models_reasoning,
    c1.models_reasoning
  );
  pushIfChanged(
    out,
    'coordinator.is_trainable',
    'Coordinator — trainable',
    c0.is_trainable !== false,
    c1.is_trainable !== false
  );
  pushIfChanged(
    out,
    'coordinator.reminder_prompt',
    'Coordinator — reminder prompt',
    c0.reminder_prompt,
    c1.reminder_prompt
  );
  pushToolDiffs(out, 'coordinator', 'Coordinator', c0, c1);

  const baseKeys = new Set(base.workers.map(workerKey));
  const proposedKeys = new Set(proposed.workers.map(workerKey));

  Array.from(proposedKeys).forEach((key) => {
    if (!baseKeys.has(key)) {
      const w = findWorker(proposed.workers, key)!;
      pushIfChanged(out, `workers.${key}.prompt`, `${w.name} — prompt (new agent)`, '', w.prompt);
    }
  });
  Array.from(baseKeys).forEach((key) => {
    if (!proposedKeys.has(key)) {
      const w = findWorker(base.workers, key)!;
      pushIfChanged(out, `workers.${key}.prompt`, `${w.name} — prompt (removed)`, w.prompt, '');
    }
  });

  Array.from(baseKeys).forEach((key) => {
    if (!proposedKeys.has(key)) return;
    const w0 = findWorker(base.workers, key)!;
    const w1 = findWorker(proposed.workers, key)!;
    const prefix = `${w1.name || key}`;
    pushIfChanged(out, `workers.${key}.name`, `${prefix} — name`, w0.name, w1.name);
    pushIfChanged(out, `workers.${key}.description`, `${prefix} — description`, w0.description, w1.description);
    pushIfChanged(out, `workers.${key}.prompt`, `${prefix} — prompt`, w0.prompt, w1.prompt);
    pushIfChanged(out, `workers.${key}.model`, `${prefix} — model`, w0.model, w1.model);
    pushIfChanged(out, `workers.${key}.temperature`, `${prefix} — temperature`, w0.temperature, w1.temperature);
    pushIfChanged(
      out,
      `workers.${key}.models_reasoning`,
      `${prefix} — reasoning`,
      w0.models_reasoning,
      w1.models_reasoning
    );
    pushIfChanged(
      out,
      `workers.${key}.is_trainable`,
      `${prefix} — trainable`,
      w0.is_trainable !== false,
      w1.is_trainable !== false
    );
    pushIfChanged(
      out,
      `workers.${key}.reminder_prompt`,
      `${prefix} — reminder prompt`,
      w0.reminder_prompt,
      w1.reminder_prompt
    );
    pushIfChanged(out, `workers.${key}.mode`, `${prefix} — mode`, w0.mode, w1.mode);
    pushToolDiffs(out, `workers.${key}`, prefix, w0, w1);
  });

  pushIfChanged(out, 'approach', 'Approach', base.approach, proposed.approach);
  pushIfChanged(
    out,
    'reminder_prompt_position',
    'Reminder position',
    base.reminder_prompt_position,
    proposed.reminder_prompt_position
  );

  const metaKeys = new Set([
    ...Object.keys(base.metaPrompts || {}),
    ...Object.keys(proposed.metaPrompts || {}),
  ]);
  metaKeys.forEach((key) => {
    pushIfChanged(
      out,
      `metaPrompts.${key}`,
      `Meta prompt — ${key}`,
      base.metaPrompts?.[key],
      proposed.metaPrompts?.[key]
    );
  });

  return out;
}

export function defaultFieldDecisions(diffs: ConfigFieldDiff[]): Record<string, FieldDecision> {
  const d: Record<string, FieldDecision> = {};
  diffs.forEach((f) => {
    d[f.path] = 'pending';
  });
  return d;
}

export function allFieldsAccepted(
  diffs: ConfigFieldDiff[],
  decisions: Record<string, FieldDecision>
): boolean {
  return diffs.every((f) => (decisions[f.path] ?? 'pending') === 'accepted');
}

/** Pending (untouched) fields become accepted when user clicks Apply changes. */
export function finalizeFieldDecisionsForApply(
  diffs: ConfigFieldDiff[],
  decisions: Record<string, FieldDecision>
): Record<string, FieldDecision> {
  const out = { ...decisions };
  diffs.forEach((d) => {
    if ((out[d.path] ?? 'pending') === 'pending') {
      out[d.path] = 'accepted';
    }
  });
  return out;
}

export function allFieldsRejected(
  diffs: ConfigFieldDiff[],
  decisions: Record<string, FieldDecision>
): boolean {
  return diffs.length > 0 && diffs.every((f) => decisions[f.path] === 'rejected');
}

/** Apply per-field decisions onto base config → merged result. */
export function mergeConfigWithFieldDecisions(
  base: AgentConfiguratorState,
  proposed: AgentConfiguratorState,
  diffs: ConfigFieldDiff[],
  decisions: Record<string, FieldDecision>
): AgentConfiguratorState {
  const merged: AgentConfiguratorState = JSON.parse(JSON.stringify(base));

  for (const diff of diffs) {
    const decision = decisions[diff.path] ?? 'pending';
    const useProposed = decision === 'accepted';
    const value = useProposed ? diff.newValue : diff.oldValue;

    if (diff.path === 'coordinator.name') merged.coordinator.name = value;
    else if (diff.path === 'coordinator.description') merged.coordinator.description = value;
    else if (diff.path === 'coordinator.prompt') merged.coordinator.prompt = value;
    else if (diff.path === 'coordinator.model') merged.coordinator.model = value;
    else if (diff.path === 'coordinator.temperature') {
      merged.coordinator.temperature = parseFloat(value) || 0.7;
    } else if (diff.path === 'coordinator.models_reasoning') {
      merged.coordinator.models_reasoning = value || undefined;
    } else if (diff.path === 'coordinator.is_trainable') {
      merged.coordinator.is_trainable = value === 'true';
    } else if (diff.path === 'coordinator.reminder_prompt') {
      merged.coordinator.reminder_prompt = value || undefined;
    } else if (diff.path.startsWith('coordinator.')) {
      const field = diff.path.split('.')[1] as ToolField;
      if ((TOOL_FIELDS as readonly string[]).includes(field)) {
        merged.coordinator[field] = value === 'true';
      }
    } else if (diff.path === 'approach') {
      merged.approach = normalizeOptimizationApproach(value);
    } else if (diff.path === 'reminder_prompt_position') {
      merged.reminder_prompt_position = value === 'after' ? 'after' : 'before';
    } else if (diff.path.startsWith('metaPrompts.')) {
      const key = diff.path.slice('metaPrompts.'.length);
      if (!merged.metaPrompts) merged.metaPrompts = {};
      if (value) merged.metaPrompts[key] = value;
      else delete merged.metaPrompts[key];
    } else if (diff.path.startsWith('workers.')) {
      const parts = diff.path.split('.');
      const key = parts[1];
      const field = parts[2];
      let worker = findWorker(merged.workers, key);
      if (!worker && useProposed) {
        const fromProposed = findWorker(proposed.workers, key);
        if (fromProposed) {
          merged.workers.push({ ...fromProposed });
          worker = findWorker(merged.workers, key);
        }
      }
      if (!worker) continue;
      if (field === 'name') worker.name = value;
      else if (field === 'description') worker.description = value;
      else if (field === 'prompt') worker.prompt = value;
      else if (field === 'model') worker.model = value;
      else if (field === 'temperature') worker.temperature = parseFloat(value) || 0.7;
      else if (field === 'models_reasoning') worker.models_reasoning = value || undefined;
      else if (field === 'is_trainable') worker.is_trainable = value === 'true';
      else if (field === 'reminder_prompt') worker.reminder_prompt = value || undefined;
      else if (field === 'mode') worker.mode = value === 'asynchronous' ? 'asynchronous' : 'synchronous';
      else if ((TOOL_FIELDS as readonly string[]).includes(field)) {
        (worker as Record<string, unknown>)[field] = value === 'true';
      }
    }
  }

  if (allFieldsRejected(diffs, decisions)) {
    return base;
  }

  return merged;
}

export function getFieldDiffMap(diffs: ConfigFieldDiff[]): Record<string, ConfigFieldDiff> {
  const m: Record<string, ConfigFieldDiff> = {};
  diffs.forEach((d) => {
    m[d.path] = d;
  });
  return m;
}
